import { StrictMode } from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import App, { type AppInitialState } from "./App";
import { initEmbedHeight } from "./embed-height";
import "./styles.css";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root in index.html");

/** Reads the initial state from <script id="__bougie-state__" type="application/json">.
 *  In dev the tag's body is the string "null" (never prerendered), so we fall
 *  back to a built-from-window default. */
function readInitialState(): AppInitialState {
  const fallback: AppInitialState = {
    url: window.location.pathname,
    offers: null,
  };
  const tag = document.getElementById("__bougie-state__");
  if (!tag || !tag.textContent) return fallback;
  try {
    const parsed = JSON.parse(tag.textContent) as AppInitialState | null;
    if (!parsed) return fallback;
    // Always re-read current pathname — direct visits to a prerendered route
    // need it correct, and push-state navigation depends on it.
    parsed.url = window.location.pathname;
    return parsed;
  } catch {
    return fallback;
  }
}

const tree = (
  <StrictMode>
    <App initial={readInitialState()} />
  </StrictMode>
);

// Hydrate only when the server actually rendered real markup. `hasChildNodes`
// is true for the `<!--app-html-->` placeholder comment node in dev, which
// would trigger hydration mismatch warnings — check for a real element child.
if (rootEl.firstElementChild != null) {
  hydrateRoot(rootEl, tree);
} else {
  createRoot(rootEl).render(tree);
}

// Auto-resize the iframe when embedded (e.g. in a GHL Custom HTML block).
initEmbedHeight();
